import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Badge, InputGroup } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import SaleBatchDetailsModal from '../components/SaleBatchDetailsModal';
import { FaEye, FaCheck, FaTimes } from 'react-icons/fa';

const Booking = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // null = all, 0 = active, 1 = confirmed, 2 = rejected
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSaleDetailsModal, setShowSaleDetailsModal] = useState(false);
  const [selectedSaleBatchId, setSelectedSaleBatchId] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const url = statusFilter !== null 
        ? `/api/bookings?status=${statusFilter}`
        : '/api/bookings';
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch bookings');
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Bronlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus, salebatchid = null) => {
    setProcessing(true);
    try {
      const response = await fetch(`/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus, salebatchid })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Status o\'zgartirishda xatolik');
      }

      toast.success(newStatus === 1 ? 'Bron tasdiqlandi' : 'Bron rad etildi');
      setShowDetailsModal(false);
      setSelectedBooking(null);
      fetchBookings();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Status o\'zgartirishda xatolik');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedBooking) return;
    
    // Pass booking data to Kassa page via localStorage
    const bookingData = {
      bookingId: selectedBooking.id,
      items: selectedBooking.items.map(item => ({
        id: item.product_id,
        productid: item.product_id,
        name: item.product_name,
        qty: parseFloat(item.quantity),
        price: parseFloat(item.price),
        batch_id: item.purchase_batch_id,
        batch_barcode: item.barcode,
        currency: 'UZS'
      })),
      delivery_price: selectedBooking.delivery_price || 0
    };

    localStorage.setItem('bookingData', JSON.stringify(bookingData));
    setShowDetailsModal(false);
    setSelectedBooking(null);
    navigate('/kassa');
  };

  const handleReject = () => {
    if (!selectedBooking) return;
    handleStatusChange(selectedBooking.id, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year}, ${hours}:${minutes}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
        return <Badge bg="warning">Faol</Badge>;
      case 1:
        return <Badge bg="success">Tasdiqlangan</Badge>;
      case 2:
        return <Badge bg="danger">Rad etilgan</Badge>;
      default:
        return <Badge bg="secondary">Noma'lum</Badge>;
    }
  };

  const handleViewDetails = async (bookingId) => {
    try {
      const response = await fetch(`/api/bookings/${bookingId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch booking details');
      const data = await response.json();
      setSelectedBooking(data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      toast.error('Bron ma\'lumotlarini yuklashda xatolik');
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Bronlar</h2>
        <div className="d-flex gap-2">
          <Button
            variant={statusFilter === null ? 'primary' : 'outline-primary'}
            onClick={() => setStatusFilter(null)}
          >
            Barchasi
          </Button>
          <Button
            variant={statusFilter === 0 ? 'warning' : 'outline-warning'}
            onClick={() => setStatusFilter(0)}
          >
            Faol
          </Button>
          <Button
            variant={statusFilter === 1 ? 'success' : 'outline-success'}
            onClick={() => setStatusFilter(1)}
          >
            Tasdiqlangan
          </Button>
          <Button
            variant={statusFilter === 2 ? 'danger' : 'outline-danger'}
            onClick={() => setStatusFilter(2)}
          >
            Rad etilgan
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Yuklanmoqda...</span>
          </div>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Sana va vaqt</th>
              <th>Xodim</th>
              <th>SellBatchID</th>
              <th>Yetkazib berish</th>
              <th>Izoh</th>
              <th>Holat</th>
              <th>Amallar</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">Bronlar topilmadi</td>
              </tr>
            ) : (
              bookings.map((booking) => (
                <tr key={booking.id}>
                  <td>{formatDate(booking.created_at)}</td>
                  <td>{booking.doer_id ? (booking.doer_name || '-') : 'Ega'}</td>
                  <td>
                    {booking.salebatchid ? (
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => {
                          setSelectedSaleBatchId(booking.salebatchid);
                          setShowSaleDetailsModal(true);
                        }}
                      >
                        {booking.salebatchid}
                      </Button>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{Number(booking.delivery_price || 0).toLocaleString()} UZS</td>
                  <td>{booking.note || '-'}</td>
                  <td>{getStatusBadge(booking.status)}</td>
                  <td>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => handleViewDetails(booking.id)}
                      className="me-2"
                    >
                      <FaEye /> Batafsil
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* Booking Details Modal */}
      <Modal show={showDetailsModal} onHide={() => !processing && setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton={!processing}>
          <Modal.Title>Bron ma'lumotlari</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <>
              <div className="mb-3">
                <strong>Sana:</strong> {formatDate(selectedBooking.created_at)}
              </div>
              <div className="mb-3">
                <strong>Xodim:</strong> {selectedBooking.doer_id ? (selectedBooking.doer_name || '-') : 'Ega'}
              </div>
              <div className="mb-3">
                <strong>Yetkazib berish:</strong> {Number(selectedBooking.delivery_price || 0).toLocaleString()} UZS
              </div>
              <div className="mb-3">
                <strong>Izoh:</strong> {selectedBooking.note || '-'}
              </div>
              <div className="mb-3">
                <strong>Holat:</strong> {getStatusBadge(selectedBooking.status)}
              </div>
              <hr />
              <h5>Mahsulotlar</h5>
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Mahsulot</th>
                    <th>Narx</th>
                    <th>Miqdor</th>
                    <th>Summa</th>
                    <th>Batch ID</th>
                    <th>Barcode</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBooking.items && selectedBooking.items.length > 0 ? (
                    selectedBooking.items.map((item, idx) => (
                      <tr key={item.id}>
                        <td>{idx + 1}</td>
                        <td>{item.product_name}</td>
                        <td>{Number(item.price).toLocaleString()} UZS</td>
                        <td>{item.quantity}</td>
                        <td>{(Number(item.price) * Number(item.quantity)).toLocaleString()} UZS</td>
                        <td>{item.batchid || item.purchase_batch_id}</td>
                        <td>{item.costing_method === 'Specific' ? (item.barcode || '-') : '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">Mahsulotlar topilmadi</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)} disabled={processing}>
            Yopish
          </Button>
          {selectedBooking && selectedBooking.status === 0 && (
            <>
              <Button
                variant="danger"
                onClick={handleReject}
                disabled={processing}
              >
                <FaTimes /> Rad etish
              </Button>
              <Button
                variant="success"
                onClick={handleConfirm}
                disabled={processing}
              >
                <FaCheck /> Kassaga o'tish
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Sale Details Modal */}
      {selectedSaleBatchId && (
        <SaleBatchDetailsModal
          show={showSaleDetailsModal}
          onHide={() => {
            setShowSaleDetailsModal(false);
            setSelectedSaleBatchId(null);
          }}
          batch={{ SellBatchID: selectedSaleBatchId }}
        />
      )}
    </div>
  );
};

export default Booking;
